import { useMemo } from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './ProgressBar.module.scss';

export const ProgressBar = ({
  current = 0,
  total = 100,
  label = '',
  showLabel = true,
  showPercentage = true,
  size = 'medium',
  variant = 'default',
  className = '',
  animated = true,
  duration = 0.5,
}) => {
  const percentage = useMemo(() => {
    if (total === 0) return 0;
    return Math.min(Math.max((current / total) * 100, 0), 100);
  }, [current, total]);

  const progressBarClasses = cn(
    styles.progressBar,
    styles[`progressBar--${size}`],
    styles[`progressBar--${variant}`],
    {
      [styles.progressBarAnimated]: animated,
      [className]: className,
    },
  );

  const progressFillClasses = cn(styles.progressFill, styles[`progressFill--${variant}`], {
    [styles.progressFillAnimated]: animated,
  });

  return (
    <div className={styles.root}>
      {showLabel && (
        <div className={styles.header}>
          <span className="secondary-text u-font-size-small u-font-weight-500">{label}</span>
          {showPercentage && (
            <span className="primary-text u-font-size-small u-font-weight-700">
              {current}/{total}
            </span>
          )}
        </div>
      )}
      <div className={progressBarClasses}>
        <div
          className={progressFillClasses}
          style={{
            width: `${percentage}%`,
            transition: animated ? `width ${duration}s ease-in-out` : 'none',
          }}
        />
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  current: PropTypes.number,
  total: PropTypes.number,
  label: PropTypes.string,
  showLabel: PropTypes.bool,
  showPercentage: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'success', 'warning', 'error', 'info']),
  statusFlow: PropTypes.bool,
  className: PropTypes.string,
  animated: PropTypes.bool,
  duration: PropTypes.number,
};
